import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { IS_PROD_BUILD } from '@y42/environment';
import { ToastService } from '@y42/toast';
import { catchError, Observable, ObservableInput, ObservedValueOf, OperatorFunction } from 'rxjs';
import { SentryService } from './sentry/sentry.service';

type ErrorToast = string | { title: string; description: string };
export type ErrorContext = object;
export type ErrorTitle<E = unknown> = ErrorToast | ((err: E | undefined) => ErrorToast | undefined);

export interface ErrorHandlerOptions<E = unknown> {
  toast?: ErrorTitle<E>;
  context?: ErrorContext;
  err?: unknown;
}

export enum Y42ErrorTag {
  FeatureType = 'y42_feature_type',
}

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService extends ErrorHandler {
  constructor(private readonly sentry: SentryService, private readonly injector: Injector) {
    super();
  }

  /**
   * This method is called by Angular - **do not use it**.
   *
   * Please use `ErrorHandlerService.error` instead.
   * @param error
   * @param options
   * @deprecated Please use `ErrorHandlerService.error` instead
   * @private
   */
  override handleError(error: unknown, options?: ErrorHandlerOptions): void {
    super.handleError(error);
    this.sentry.captureException(error);
    this.handleErrorHandlerOptions(error, options);
  }

  /**
   * Use this in place of the regular `rxjs/catchError`
   *
   * @param errorMessage the technical error message that will be displayed in Sentry
   * @param selector function that returns a new Observable value
   * @params options provide additional context or a toast message to be displayed
   */
  catchError<I, O extends ObservableInput<unknown>>(
    errorMessage: string,
    selector: (err: unknown, caught: Observable<I>) => O,
    options?: ErrorHandlerOptions,
  ): OperatorFunction<I, I | ObservedValueOf<O>> {
    return catchError((err: unknown, caught) => {
      this.error(errorMessage, {
        toast: options?.toast,
        context: options?.context
          ? {
              ...options.context,
              errorObject: err,
            }
          : (err as object),
        err,
      });
      return selector(err, caught);
    });
  }

  /**
   * Reports an event to Sentry and marks it as an error
   * @param errorMessage
   * @param options
   */
  error(errorMessage: string, options?: ErrorHandlerOptions): void {
    console.error(errorMessage, options?.context || '');
    this.captureMessage({
      errorMessage,
      level: 'error',
      options,
    });
    if (!IS_PROD_BUILD) {
      // This will produce a stacktrace in the console, which makes debugging a lot easier
      console.error(new Error(errorMessage));
    }
  }

  /**
   * Reports an event to Sentry and marks it as a log
   * @param errorMessage
   * @param options
   */
  log(errorMessage: string, options?: ErrorHandlerOptions): void {
    if (!IS_PROD_BUILD) {
      console.warn(errorMessage, options?.context || '');
    }
    this.captureMessage({
      errorMessage,
      level: 'log',
      options,
    });
  }

  setTag(key: string, value: string | null): void {
    this.sentry.setTag(key, value);
  }

  private captureMessage({
    errorMessage,
    level,
    options,
  }: {
    errorMessage: Parameters<SentryService['captureMessage']>[0];
    level: 'error' | 'warning' | 'log';
    options?: ErrorHandlerOptions;
  }): void {
    const { context } = options || {};
    this.sentry.captureMessage(errorMessage, (scope) => {
      scope.setExtras(context as Record<string, unknown>);
      scope.setLevel(level);
      return scope;
    });

    this.handleErrorHandlerOptions(options?.err, options);
  }

  private handleErrorHandlerOptions(err: unknown | undefined, options?: ErrorHandlerOptions): void {
    const toast = options?.toast;
    const message = toast ? (typeof toast === 'function' ? toast(err) : toast) : undefined;

    if (message) {
      this.injector.get(ToastService).error(message);
    }
  }
}
